import React from "react"
import { Link } from "react-router-dom"
import { ArrowUpRight } from "lucide-react"

const InstagramIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
)
const LinkedinIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/>
  </svg>
)
const YoutubeIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22.54 6.42a2.78 2.78 0 0 0-1.95-1.96C18.88 4 12 4 12 4s-6.88 0-8.59.46a2.78 2.78 0 0 0-1.95 1.96A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.41 19.1C5.12 19.56 12 19.56 12 19.56s6.88 0 8.59-.46a2.78 2.78 0 0 0 1.95-1.95 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/><polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
  </svg>
)

const footerLinks = {
  Studio: [
    { label: "Our Story", href: "/about" },
    { label: "Team", href: "/about" },
    { label: "Careers", href: "/contact" },
    { label: "Press", href: "/journal" },
  ],
  Work: [
    { label: "Architecture", href: "/works" },
    { label: "Interior Design", href: "/works" },
    { label: "3D Visualization", href: "/works" },
    { label: "Urban Planning", href: "/works" },
  ],
  Connect: [
    { label: "Contact", href: "/contact" },
    { label: "Start a Project", href: "/contact" },
    { label: "Journal", href: "/journal" },
    { label: "Services", href: "/services" },
  ],
}

export default function Footer() {
  return (
    <footer className="bg-stone-900 border-t border-stone-800">
      {/* Main Footer */}
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-8">
          {/* Brand */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-3 group mb-6">
              <img
                src="/mainlogo.png"
                alt="Bastu Binyash"
                className="h-9 w-auto object-contain"
              />
              <div className="leading-none">
                <span className="block font-['Cormorant_Garamond'] text-xl font-semibold tracking-[0.2em] text-stone-100 uppercase">
                  Bastu Binyash
                </span>
              </div>
            </Link>
            <p className="font-['Inter'] text-sm text-stone-400 leading-relaxed max-w-xs mb-8">
              Crafting environments that transcend the ordinary. Where spatial poetry meets precision engineering.
            </p>
            <div className="flex gap-4">
              <a href="#" className="w-9 h-9 border border-stone-700 flex items-center justify-center text-stone-400 hover:border-amber-400 hover:text-amber-400 transition-colors">
                <InstagramIcon />
              </a>
              <a href="#" className="w-9 h-9 border border-stone-700 flex items-center justify-center text-stone-400 hover:border-amber-400 hover:text-amber-400 transition-colors">
                <LinkedinIcon />
              </a>
              <a href="#" className="w-9 h-9 border border-stone-700 flex items-center justify-center text-stone-400 hover:border-amber-400 hover:text-amber-400 transition-colors">
                <YoutubeIcon />
              </a>
            </div>
          </div>

          {/* Links */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="font-['Space_Grotesk'] text-[10px] tracking-[0.3em] text-amber-400 uppercase mb-5 font-medium">
                {category}
              </h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="font-['Inter'] text-sm text-stone-400 hover:text-stone-200 transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Newsletter */}
        <div className="mt-16 pt-12 border-t border-stone-800">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
            <div>
              <h3 className="font-['Cormorant_Garamond'] text-2xl font-light text-stone-100 mb-2">
                Stay in the conversation
              </h3>
              <p className="font-['Inter'] text-sm text-stone-500">
                Design insights, project reveals, and studio news.
              </p>
            </div>
            <form
              className="flex gap-0 w-full lg:w-auto"
              onSubmit={(e) => e.preventDefault()}
            >
              <input
                type="email"
                placeholder="Your email address"
                className="bg-stone-800 border border-stone-700 text-stone-200 placeholder-stone-500 px-5 py-3 text-sm font-['Inter'] focus:outline-none focus:border-amber-400 transition-colors w-full lg:w-72"
              />
              <button
                type="submit"
                className="bg-amber-400 text-stone-950 px-5 py-3 font-['Space_Grotesk'] text-xs tracking-[0.15em] font-medium hover:bg-amber-300 transition-colors whitespace-nowrap flex items-center gap-2"
              >
                SUBSCRIBE <ArrowUpRight size={14} />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-stone-800">
        <div className="max-w-screen-2xl mx-auto px-6 lg:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="font-['Inter'] text-xs text-stone-600">
            © {new Date().getFullYear()} Bastu Binyash. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="font-['Inter'] text-xs text-stone-600 hover:text-stone-400 transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="font-['Inter'] text-xs text-stone-600 hover:text-stone-400 transition-colors">
              Terms of Use
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
}
